import { useEffect, useState, type FormEvent } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { getAuthConfig, getMe, googleLoginUrl } from "@/api";
import { useAuth } from "@/auth";
import { BrandMark } from "@/components/BrandMark";

// Maps the ?error=... the OAuth callback redirects with onto a friendly message.
const OAUTH_ERRORS: Record<string, string> = {
  denied: "This Google account isn't authorized for the admin.",
  unverified: "Your Google email isn't verified.",
  signin_failed: "Google sign-in failed. Please try again.",
};

export function Login() {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const oauthError = OAUTH_ERRORS[searchParams.get("error") ?? ""] ?? null;
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [googleEnabled, setGoogleEnabled] = useState(false);

  useEffect(() => {
    let active = true;
    getAuthConfig()
      .then((cfg) => active && setGoogleEnabled(cfg.google))
      .catch(() => active && setGoogleEnabled(false));
    return () => {
      active = false;
    };
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await getMe({ username, password });
      login({ username, password });
      navigate("/", { replace: true });
    } catch {
      setError("Invalid credentials.");
    } finally {
      setBusy(false);
    }
  };

  // Login lives OUTSIDE RequireAuth, so a live session hitting /login (bookmark,
  // refresh, back button) would otherwise see the form. Bounce to the app once
  // actually authenticated — both lanes (Basic creds, or an active Google
  // cookie → status "cookie"). We do NOT redirect during the "checking" probe,
  // so a logged-out user still sees the form immediately. Placed after all hooks
  // (rules of hooks) so the early return never skips one.
  if (isAuthenticated) return <Navigate to="/" replace />;

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <form
        onSubmit={onSubmit}
        className="rise card relative w-full max-w-sm overflow-hidden p-8"
      >
        <span
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-lime to-transparent"
        />
        <div className="flex justify-center">
          <BrandMark variant="hero" tag={null} />
        </div>
        <p className="mb-8 mt-4 text-center font-display text-[11px] uppercase tracking-[0.32em] text-faint">
          Admin Console
        </p>

        {googleEnabled && (
          <a
            href={googleLoginUrl()}
            className="btn-primary mb-6 flex w-full items-center justify-center gap-2"
          >
            Sign in with Google
          </a>
        )}

        <div className="space-y-3">
          <input
            aria-label="Username"
            className="field"
            placeholder="Username"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <input
            aria-label="Password"
            type="password"
            className="field"
            placeholder="Password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        {(error ?? oauthError) && (
          <p role="alert" className="mt-3 text-sm text-coral">
            {error ?? oauthError}
          </p>
        )}

        <button type="submit" disabled={busy} className="btn-primary mt-6 w-full">
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}
